import { CheckCircle2, XCircle, AlertTriangle, CircleDashed, Loader2, Pause } from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { TestLabScenario, TestRunStatus } from "@/types/test_lab"

type Props = {
  scenario: TestLabScenario
  selected: boolean
  onSelect: () => void
  onRun: () => void          // run / re-run, depending on whether latestRunId exists
  busy: boolean              // true when this scenario has an in-flight run start
}

// One row in the scenario list. Big status icon on the left, human name +
// 1-line goal as the body, turns/assertions/timestamp as the footer.
export function ScenarioCard({ scenario, selected, onSelect, onRun, busy }: Props) {
  const status = scenario.latestRunStatus
  const hasRun = scenario.latestRunId != null

  const handleRun = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRun()
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onSelect() } }}
      className={cn(
        "grid w-full cursor-pointer grid-cols-[28px_1fr_auto] items-start gap-3 rounded-card border px-3 py-3 text-left text-sm transition outline-none",
        selected
          ? "border-brand-red bg-brand-redSoft/20 ring-1 ring-brand-red/40"
          : "border-border bg-app-panel hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40"
      )}
    >
      <StatusIcon status={status} hasRun={hasRun} />

      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-app-text">{scenario.name || scenario.key}</span>
          <ModeChip mode={scenario.mode} />
        </div>
        {scenario.goal && (
          <p className="line-clamp-2 text-xs text-app-muted">{scenario.goal}</p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-app-subtle">
          <span>{scenario.turnsCount} turns</span>
          <span>·</span>
          <span>{scenario.assertionsCount} assertions</span>
          {scenario.latestRunAt && (
            <>
              <span>·</span>
              <span>{fmtCompactRelative(scenario.latestRunAt)}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-2">
        <StatusPill status={status} hasRun={hasRun} skipReason={scenario.skipReason} />
        {scenario.executable && (
          <button
            type="button"
            onClick={handleRun}
            disabled={busy || status === "running" || status === "pending"}
            className="rounded-control border border-brand-red/40 bg-transparent px-2 py-0.5 text-[11px] font-medium text-brand-red transition hover:bg-brand-red/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy || status === "running" || status === "pending"
              ? "Running…"
              : hasRun ? "Re-run" : "Run"}
          </button>
        )}
      </div>
    </div>
  )
}

function ModeChip({ mode }: { mode: "isolation" | "integration" }) {
  const isInt = mode === "integration"
  return (
    <span className={cn(
      "shrink-0 rounded-control border px-1.5 py-0.5 text-[9px] uppercase tracking-wide",
      isInt
        ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
        : "border-app-muted/30 bg-white/5 text-app-muted",
    )}>
      {isInt ? "int" : "iso"}
    </span>
  )
}

function StatusIcon({ status, hasRun }: { status: TestRunStatus | null; hasRun: boolean }) {
  if (!hasRun) return <CircleDashed size={20} className="text-app-muted" />
  if (status === "passed")  return <CheckCircle2 size={20} className="text-status-success" />
  if (status === "failed")  return <XCircle      size={20} className="text-status-error" />
  if (status === "errored") return <AlertTriangle size={20} className="text-status-warning" />
  if (status === "skipped") return <Pause        size={20} className="text-app-muted" />
  if (status === "running" || status === "pending")
    return <Loader2 size={20} className="animate-spin text-app-muted" />
  return <CircleDashed size={20} className="text-app-muted" />
}

function StatusPill({ status, hasRun, skipReason }:
  { status: TestRunStatus | null; hasRun: boolean; skipReason: string | null }) {
  if (skipReason) {
    return <span className="rounded-control border border-app-muted/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-app-muted">Manual</span>
  }
  if (!hasRun) {
    return <span className="text-[10px] uppercase tracking-wide text-app-subtle">Not run</span>
  }
  const tone = (() => {
    switch (status) {
      case "passed":  return "border-status-success/40 bg-status-success/10 text-status-success"
      case "failed":  return "border-status-error/40 bg-status-error/10 text-status-error"
      case "errored": return "border-status-warning/40 bg-status-warning/10 text-status-warning"
      case "running":
      case "pending": return "border-app-muted/40 bg-app-bg text-app-muted"
      case "skipped": return "border-app-muted/40 bg-app-bg text-app-muted"
      default:        return "border-app-muted/40 bg-app-bg text-app-muted"
    }
  })()
  const label = status === "pending" ? "Queued" : status ?? "—"
  return (
    <span className={cn("rounded-control border px-2 py-0.5 text-[10px] uppercase tracking-wide", tone)}>
      {label}
    </span>
  )
}
